import React from "react";
import { Capacitor } from "@capacitor/core";
import { brand } from "../../theme/brand";
import { StatusPill } from "../components/StatusPill";
import type { OuraConnection } from "../../supabase/oura";
import type { ActivityBurnSource } from "../../types/entries";
import { useHealthKit } from "../../hooks/useHealthKit";
import { formatSyncTime } from "../../utils/formatSyncTime";

const btn: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 10,
  background: "rgba(255,255,255,0.05)",
  color: "white",
  fontSize: "0.88rem",
  padding: "0.58rem 0.9rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  cursor: "pointer",
  fontWeight: 700,
};

const infoRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(76px, 110px) minmax(0, 1fr)",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.45rem 0",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const dataSourcePill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "0.2rem 0.55rem",
  borderRadius: 999,
  fontSize: "0.77rem",
  fontWeight: 700,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.06)",
  color: "rgba(255,255,255,0.75)",
  whiteSpace: "nowrap" as const,
};

type Props = {
  ouraConnection?: OuraConnection | null;
  ouraStatus?: "idle" | "syncing" | "error";
  ouraRedirectError?: string | null;
  onOuraConnect?: () => Promise<void>;
  onOuraDisconnect?: () => Promise<void>;
  onOuraSyncNow?: () => Promise<void>;
  onOuraToggleAuthoritative?: (value: boolean) => Promise<void>;
  // TODO: wire to a dedicated auto_sync backend column when available.
  // For now, toggling auto-sync off sets enabled=false (same as disconnecting).
  onOuraToggleAutoSync?: (value: boolean) => Promise<void>;
  supabaseUserId: string | null;
  isDemoMode: boolean;
  // Activity burn source (shared between Oura and Apple Health cards)
  activityBurnSource?: ActivityBurnSource;
  onActivityBurnSourceChange?: (val: ActivityBurnSource) => Promise<void>;
  // Apple Health
  healthKitEnabled?: boolean;
  onHealthKitToggle?: (val: boolean) => Promise<void>;
  healthKitStatus?: "idle" | "syncing" | "error";
  healthKitLastSyncAt?: string | null;
  onHealthKitSyncNow?: () => Promise<void>;
};

function formatSyncStatus(status?: "idle" | "syncing" | "error", lastSyncAt?: string | null): string {
  if (status === "syncing") return "Syncing";
  if (status === "error") return "Error";
  return lastSyncAt ? "Synced" : "Idle";
}

// ---- IntegrationRow ----

type RowProps = {
  iconSrc: string;
  iconBg: string;
  iconBorder: string;
  title: string;
  subtitle: string;
  subtitleExtra?: string;
  statusVariant: "connected" | "not-connected" | "syncing" | "idle" | "error";
  statusLabel: string;
  onClick: () => void;
  isLast?: boolean;
};

function IntegrationRow({
  iconSrc, iconBg, iconBorder,
  title, subtitle, subtitleExtra,
  statusVariant, statusLabel,
  onClick, isLast,
}: RowProps) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      style={{
        padding: "0.82rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.06)",
        background: hovered ? "rgba(255,255,255,0.04)" : "transparent",
        textAlign: "left",
        width: "100%",
        minHeight: 56,
        boxSizing: "border-box",
        cursor: "pointer",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon block */}
      <div style={{
        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
        overflow: "hidden", background: iconBg,
      }}>
        <img
          src={iconSrc}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          alt=""
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>

      {/* Text block */}
      <div style={{ flex: 1, display: "grid", gap: "0.1rem", minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: "0.93rem", color: "rgba(255,255,255,0.92)" }}>
          {title}
        </span>
        <span style={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.4 }}>
          {subtitle}
        </span>
        {subtitleExtra && (
          <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.25)" }}>
            {subtitleExtra}
          </span>
        )}
      </div>

      {/* Right side */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
        <StatusPill variant={statusVariant} label={statusLabel} />
        <span style={{ fontSize: 18, color: "rgba(255,255,255,0.22)", marginLeft: 4 }}>›</span>
      </div>
    </button>
  );
}

// ---- IntegrationsSection ----

export function IntegrationsSection({
  ouraConnection,
  ouraStatus,
  ouraRedirectError,
  onOuraConnect,
  onOuraDisconnect,
  onOuraSyncNow,
  onOuraToggleAuthoritative,
  onOuraToggleAutoSync,
  supabaseUserId,
  isDemoMode,
  activityBurnSource,
  onActivityBurnSourceChange,
  healthKitEnabled,
  onHealthKitToggle,
  healthKitStatus,
  healthKitLastSyncAt,
  onHealthKitSyncNow,
}: Props) {
  const disableWrites = isDemoMode;
  const isConnected = !!ouraConnection?.enabled;
  const isNative = Capacitor.isNativePlatform();

  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );
  const [connectError, setConnectError] = React.useState<string | null>(null);
  const [autoSync, setAutoSync] = React.useState(true);
  const [selected, setSelected] = React.useState<"oura" | "healthkit" | null>(null);

  // Derive checkbox states from activityBurnSource
  const ouraChecked = activityBurnSource === "oura" || activityBurnSource === "best";
  const hkChecked = activityBurnSource === "healthkit" || activityBurnSource === "best";

  // Reset auto-sync state when connection changes
  React.useEffect(() => {
    setAutoSync(isConnected);
  }, [isConnected]);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const { requestPermission } = useHealthKit();

  const handleConnect = async () => {
    if (!onOuraConnect || disableWrites || !supabaseUserId) return;
    setConnectError(null);
    try {
      await onOuraConnect();
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleAutoSyncToggle = async (value: boolean) => {
    setAutoSync(value);
    if (onOuraToggleAutoSync) {
      try {
        await onOuraToggleAutoSync(value);
      } catch {
        setAutoSync(!value); // revert on error
      }
    }
  };

  // Derive new ActivityBurnSource from two boolean checkbox values
  const deriveSource = (oura: boolean, hk: boolean): ActivityBurnSource => {
    if (oura && hk) return "best";
    if (oura) return "oura";
    if (hk) return "healthkit";
    return "manual";
  };

  const handleOuraActivityToggle = async (checked: boolean) => {
    if (disableWrites) return;
    const next = deriveSource(checked, hkChecked);
    await onActivityBurnSourceChange?.(next);
  };

  const handleHkActivityToggle = async (checked: boolean) => {
    if (disableWrites) return;
    const next = deriveSource(ouraChecked, checked);
    await onActivityBurnSourceChange?.(next);
  };

  const handleEnableHealthKit = async () => {
    if (disableWrites) return;
    const { granted } = await requestPermission();
    if (granted) {
      await onHealthKitToggle?.(true);
    }
  };

  const ouraStatusVariant = ouraStatus === "syncing"
    ? "syncing"
    : ouraStatus === "error"
    ? "error"
    : isConnected
    ? "connected"
    : "not-connected";

  const hkStatusVariant = healthKitStatus === "syncing"
    ? "syncing"
    : healthKitStatus === "error"
    ? "error"
    : healthKitEnabled
    ? "connected"
    : "not-connected";

  const maxWidth = isMobile ? "100%" : 700;

  // ---- Level 1: List ----

  const renderList = () => (
    <div style={{ display: "grid", gap: 0, width: "100%", maxWidth }}>
      <p style={{
        margin: "0 0 0.75rem 0",
        fontSize: "0.78rem", fontWeight: 700,
        color: "rgba(255,255,255,0.38)",
        textTransform: "uppercase", letterSpacing: "0.08em",
      }}>
        Connected apps
      </p>

      <div style={{
        border: "1px solid rgba(148,163,184,0.2)",
        background: "rgba(15,23,42,0.45)",
        borderRadius: 14,
        overflow: "hidden",
      }}>
        <IntegrationRow
          iconSrc="/Icons/oura.PNG"
          iconBg="rgba(255,255,255,0.04)"
          iconBorder="1px solid rgba(255,255,255,0.08)"
          title="Oura Ring"
          subtitle="Sync calories and activity from Oura"
          statusVariant={ouraStatusVariant}
          statusLabel={
            ouraStatus === "syncing" ? "Syncing…"
            : ouraStatus === "error" ? "Needs attention"
            : isConnected ? "Connected"
            : "Connect"
          }
          onClick={() => setSelected("oura")}
        />
        <IntegrationRow
          iconSrc="/Icons/apple-health.PNG"
          iconBg="rgba(255,255,255,0.04)"
          iconBorder="1px solid rgba(255,255,255,0.08)"
          title="Apple Health"
          subtitle="Sync weight and calories from Apple Health"
          subtitleExtra={!isNative ? "iOS only" : undefined}
          statusVariant={hkStatusVariant}
          statusLabel={
            healthKitStatus === "syncing" ? "Syncing…"
            : healthKitStatus === "error" ? "Needs attention"
            : healthKitEnabled ? "Connected"
            : "Connect"
          }
          onClick={() => setSelected("healthkit")}
          isLast
        />
      </div>

      <p style={{
        margin: "0.75rem 0 0",
        fontSize: "0.77rem",
        color: "rgba(255,255,255,0.32)",
        lineHeight: 1.5,
      }}>
        You can always override synced data.
      </p>
    </div>
  );

  // ---- Shared detail shell ----

  const renderDetailShell = (
    iconSrc: string,
    iconBg: string,
    iconBorder: string,
    title: string,
    statusVariant: "connected" | "not-connected" | "syncing" | "idle" | "error",
    statusLabel: string,
    content: React.ReactNode,
  ) => (
    <div style={{ width: "100%", maxWidth }}>
      <button
        style={{
          border: "none", background: "transparent",
          color: brand.accent.primary,
          fontSize: "0.88rem", fontWeight: 700,
          cursor: "pointer", padding: "0 0 1.1rem 0",
          display: "block",
        }}
        onClick={() => setSelected(null)}
      >
        ‹ Apps &amp; Devices
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
        <div style={{
          width: 44, height: 44, borderRadius: 11, flexShrink: 0,
          overflow: "hidden", background: iconBg,
        }}>
          <img
            src={iconSrc}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            alt=""
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
        <span style={{ fontWeight: 800, fontSize: "1.05rem", color: "rgba(255,255,255,0.95)", flex: 1 }}>
          {title}
        </span>
        <StatusPill variant={statusVariant} label={statusLabel} />
      </div>

      <div style={{
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(15,23,42,0.45)",
        borderRadius: 14,
        padding: isMobile ? "0.9rem" : "1rem 1.1rem",
      }}>
        {content}
      </div>
    </div>
  );

  // ---- Level 2: Oura detail ----

  const renderOuraDetail = () => {
    const statusLabel = ouraStatus === "syncing" ? "Syncing…"
      : ouraStatus === "error" ? "Needs attention"
      : isConnected ? "Connected"
      : "Not connected";

    const content = !isConnected ? (
      /* NOT CONNECTED state */
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <p style={{ margin: 0, color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", lineHeight: 1.5 }}>
          Sync activity burn to improve TDEE estimates.
        </p>

        {!supabaseUserId && (
          <div style={{ color: brand.state.warning, fontSize: "0.83rem", padding: "0.5rem 0.7rem", background: "rgba(245,158,11,0.08)", borderRadius: 8, border: "1px solid rgba(245,158,11,0.2)" }}>
            Sign in first to enable Oura.
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            style={{
              ...btn,
              background: (disableWrites || !supabaseUserId) ? "rgba(59,130,246,0.3)" : brand.accent.primary,
              borderColor: "rgba(59,130,246,0.5)",
              color: "white",
              opacity: (disableWrites || !supabaseUserId) ? 0.5 : 1,
              cursor: (disableWrites || !supabaseUserId) ? "not-allowed" : "pointer",
            }}
            onClick={() => { void handleConnect(); }}
            disabled={disableWrites || !supabaseUserId}
            title={!supabaseUserId ? "Sign in to your account first" : disableWrites ? "Disabled in Demo" : ""}
          >
            Connect Oura
          </button>
        </div>

        {connectError && (
          <div style={{ color: "#fca5a5", fontSize: "0.83rem" }}>{connectError}</div>
        )}
        {ouraRedirectError && !connectError && (
          <div style={{ color: "#fca5a5", fontSize: "0.83rem" }}>Connection failed: {ouraRedirectError}</div>
        )}
      </div>
    ) : (
      /* CONNECTED state */
      <div style={{ display: "grid", gap: 0 }}>

        {/* Row 1 — Last sync */}
        <div style={infoRow}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.84rem" }}>Last sync</span>
          <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.83rem" }}>
            {formatSyncTime(ouraConnection.lastSyncAt)}
          </span>
        </div>

        {/* Row 2 — Activity */}
        <div style={{ ...infoRow, borderBottom: "none", alignItems: "start" }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.84rem", paddingTop: "0.15rem" }}>Activity</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: "#86efac", flexShrink: 0, display: "inline-block" }} />
            <span style={{ color: "#86efac", fontSize: "0.83rem", fontWeight: 600 }}>Active</span>
          </div>
          {!healthKitEnabled ? (
            <span style={{ gridColumn: "2", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
              TDEE + active calories
            </span>
          ) : activityBurnSource === "healthkit" ? (
            <span style={{ gridColumn: "2", display: "flex", alignItems: "center", gap: "0.4rem", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
              <span>Health</span>
              <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
              <button
                style={{ border: "none", background: "transparent", padding: 0, color: brand.accent.primary, fontSize: "0.83rem", fontWeight: 700, cursor: "pointer" }}
                onClick={() => { void onActivityBurnSourceChange?.("best"); }}
              >
                Switch to Oura
              </button>
            </span>
          ) : (
            <span style={{ gridColumn: "2", display: "flex", alignItems: "center", gap: "0.4rem", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
              <span>Oura</span>
              <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
              <button
                style={{ border: "none", background: "transparent", padding: 0, color: brand.accent.primary, fontSize: "0.83rem", fontWeight: 700, cursor: "pointer" }}
                onClick={() => { void onActivityBurnSourceChange?.("healthkit"); }}
              >
                Switch to Health
              </button>
            </span>
          )}
        </div>

        {/* Actions */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem", display: "grid", gap: "0.6rem" }}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              style={{ ...btn, background: brand.accent.primary, borderColor: "rgba(59,130,246,0.5)", color: "white" }}
              onClick={() => { void onOuraSyncNow?.(); }}
              disabled={disableWrites || ouraStatus === "syncing"}
              title={disableWrites ? "Disabled in Demo" : "Pull last 7 days from Oura"}
            >
              {ouraStatus === "syncing" ? "Syncing…" : "Sync now"}
            </button>
            <button
              style={{ ...btn, background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.22)", color: "#fca5a5" }}
              onClick={() => { void onOuraDisconnect?.(); }}
              disabled={disableWrites}
              title={disableWrites ? "Disabled in Demo" : "Remove Oura connection"}
            >
              Disconnect
            </button>
          </div>
          <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem" }}>
            Your existing entries are kept.
          </span>
        </div>

      </div>
    );

    return renderDetailShell(
      "/Icons/oura.PNG",
      "rgba(255,255,255,0.04)",
      "1px solid rgba(255,255,255,0.08)",
      "Oura Ring",
      ouraStatusVariant,
      statusLabel,
      content,
    );
  };

  // ---- Level 2: Apple Health detail ----

  const renderAppleHealthDetail = () => {
    const statusLabel = healthKitStatus === "syncing" ? "Syncing…"
      : healthKitStatus === "error" ? "Needs attention"
      : healthKitEnabled ? "Connected"
      : "Not connected";

    const content = !healthKitEnabled ? (
      /* NOT CONNECTED */
      <div style={{ display: "grid", gap: "1rem" }}>
        <p style={{ margin: 0, fontSize: "0.88rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.55 }}>
          Sync weight and burn from Health and Apple Watch.
        </p>
        {healthKitStatus === "error" ? (
          <p style={{ margin: 0, fontSize: "0.82rem", color: "rgba(252,165,165,0.92)", lineHeight: 1.5 }}>
            Apple Health access appears disabled. Reconnect to resume syncing.
          </p>
        ) : null}
        {isNative ? (
          <button
            style={{
              ...btn,
              background: disableWrites ? "rgba(59,130,246,0.3)" : brand.accent.primary,
              borderColor: "rgba(59,130,246,0.5)",
              color: "white",
              opacity: disableWrites ? 0.5 : 1,
              cursor: disableWrites ? "not-allowed" : "pointer",
              width: "max-content",
            }}
            onClick={() => { void handleEnableHealthKit(); }}
            disabled={disableWrites}
            title={disableWrites ? "Disabled in Demo" : ""}
          >
            Connect Apple Health
          </button>
        ) : (
          <p style={{ margin: 0, fontSize: "0.83rem", color: "rgba(255,255,255,0.32)" }}>
            Open the iOS app to connect Apple Health.
          </p>
        )}
      </div>
    ) : (
      /* CONNECTED */
      <div style={{ display: "grid", gap: 0 }}>

        {/* Row 1 — Last sync */}
        <div style={infoRow}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.84rem" }}>Last sync</span>
          <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.83rem" }}>
            {formatSyncTime(healthKitLastSyncAt)}
          </span>
        </div>

        {/* Row 2 — Weight */}
        <div style={{ ...infoRow, alignItems: "start" }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.84rem", paddingTop: "0.15rem" }}>Weight</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: "#86efac", flexShrink: 0, display: "inline-block" }} />
            <span style={{ color: "#86efac", fontSize: "0.83rem", fontWeight: 600 }}>Active</span>
          </div>
          <span style={{ gridColumn: "2", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
            Synced from Health
          </span>
        </div>

        {/* Row 3 — Activity */}
        <div style={{ ...infoRow, borderBottom: "none", alignItems: "start" }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.84rem", paddingTop: "0.15rem" }}>Activity</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: "#86efac", flexShrink: 0, display: "inline-block" }} />
            <span style={{ color: "#86efac", fontSize: "0.83rem", fontWeight: 600 }}>Active</span>
          </div>
          {!ouraConnection?.enabled ? (
            <span style={{ gridColumn: "2", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
              Active calories from Health
            </span>
          ) : activityBurnSource === "healthkit" ? (
            <span style={{ gridColumn: "2", display: "flex", alignItems: "center", gap: "0.4rem", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
              <span>Health</span>
              <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
              <button
                style={{ border: "none", background: "transparent", padding: 0, color: brand.accent.primary, fontSize: "0.83rem", fontWeight: 700, cursor: "pointer" }}
                onClick={() => { void onActivityBurnSourceChange?.("best"); }}
              >
                Switch to Oura
              </button>
            </span>
          ) : (
            <span style={{ gridColumn: "2", display: "flex", alignItems: "center", gap: "0.4rem", color: "rgba(255,255,255,0.38)", fontSize: "0.76rem", lineHeight: 1.4 }}>
              <span>Oura preferred</span>
              <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
              <button
                style={{ border: "none", background: "transparent", padding: 0, color: brand.accent.primary, fontSize: "0.83rem", fontWeight: 700, cursor: "pointer" }}
                onClick={() => { void onActivityBurnSourceChange?.("healthkit"); }}
              >
                Switch to Health
              </button>
            </span>
          )}
        </div>

        {/* Actions */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem", display: "flex", gap: "0.5rem" }}>
          <button
            style={{ ...btn, background: brand.accent.primary, borderColor: "rgba(59,130,246,0.5)", color: "white" }}
            onClick={() => { void onHealthKitSyncNow?.(); }}
            disabled={disableWrites || healthKitStatus === "syncing"}
            title={disableWrites ? "Disabled in Demo" : "Pull last 30 days from Apple Health"}
          >
            {healthKitStatus === "syncing" ? "Syncing…" : "Sync now"}
          </button>
          <button
            style={{ ...btn, background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.22)", color: "#fca5a5" }}
            onClick={() => { void onHealthKitToggle?.(false); }}
            disabled={disableWrites}
            title={disableWrites ? "Disabled in Demo" : "Disable Apple Health integration"}
          >
            Disable
          </button>
        </div>

      </div>
    );

    return renderDetailShell(
      "/Icons/apple-health.PNG",
      "rgba(255,255,255,0.04)",
      "1px solid rgba(255,255,255,0.08)",
      "Apple Health",
      hkStatusVariant,
      statusLabel,
      content,
    );
  };

  // ---- Top-level render ----

  if (selected === null) return renderList();
  if (selected === "oura") return renderOuraDetail();
  return renderAppleHealthDetail();
}
